import { Box, Stack, Typography } from '@mui/material';
import BorderedButton from '../components/common/buttons/BorderedButton';
import { useNavigate } from 'react-router-dom';

import { YourNasemScaleWithGuidelinesProps } from '../types/reports';
import { useThemeContext } from '../contexts/BaseThemeContextProvider';

export default function PFASAdviceGuidance({ nasemLevel, displayNasemLevel }: YourNasemScaleWithGuidelinesProps) {
  const { theme } = useThemeContext();

  const renderScreeningAdvice = () => {
    return (
      <Typography
        variant="body2"
        sx={{
          fontSize: '12px',
          color: theme.custom.report.pfasAdviceGuidance.pfasMediumNasemLevelAdvice.renderScreeningAdvice.color,
          fontWeight: 400,
        }}
      >
        Prioritize screening for dyslipidemia with a lipid panel (every 4 to 6 years over age 20) as recommended by the
        &nbsp;
        <a
          rel="noreferrer"
          style={{
            color: theme.custom.report.pfasAdviceGuidance.pfasMediumNasemLevelAdvice.renderScreeningAdvice.linkColor,
          }}
          target="_blank"
          href="https://www.heart.org/"
        >
          American Heart Association (AHA)
        </a>
        .
      </Typography>
    );
  };

  const renderHypertensiveAdvice = () => {
    return (
      <Typography
        variant="body2"
        sx={{
          fontSize: '12px',
          color: theme.custom.report.pfasAdviceGuidance.pfasMediumNasemLevelAdvice.renderHypertensiveAdvice.color,
          fontWeight: 400,
        }}
      >
        Screen for hypertensive disorders of pregnancy at all prenatal visits per the{' '}
        <a
          rel="noreferrer"
          style={{
            color: theme.custom.report.pfasAdviceGuidance.pfasMediumNasemLevelAdvice.renderHypertensiveAdvice.linkColor,
          }}
          target="_blank"
          href="https://www.acog.org/"
        >
          American College of Obstetricians and Gynecologists (ACOG)
        </a>
        .
      </Typography>
    );
  };

  const renderBreastCancerAdvice = () => {
    return (
      <Typography
        variant="body2"
        sx={{
          fontSize: '12px',
          color: theme.custom.report.pfasAdviceGuidance.pfasMediumNasemLevelAdvice.renderBreastCancerAdvice.color,
          fontWeight: 400,
        }}
      >
        Screen for breast cancer based on clinical practice guidelines based on age and other risk factors such as those
        recommended by the{' '}
        <a
          rel="noreferrer"
          style={{
            color: theme.custom.report.pfasAdviceGuidance.pfasMediumNasemLevelAdvice.renderBreastCancerAdvice.linkColor,
          }}
          target="_blank"
          href="https://www.uspreventiveservicestaskforce.org/uspstf/"
        >
          US Preventive Services Task Force (USPSTF)
        </a>
        .
      </Typography>
    );
  };

  const mediumNasemLevelAdvices = [renderScreeningAdvice, renderHypertensiveAdvice, renderBreastCancerAdvice];

  const boxesStyles = {
    background: theme.custom.report.pfasAdviceGuidance.box.backgroundColor,
    marginBottom: '8px',
    width: { xs: '100%', sm: '48%', md: '32%' },
    padding: '24px',
    borderRadius: '24px',
  };

  const PFASLowNasemLevelAdvice = ({ nasemLevel, displayNasemLevel }: YourNasemScaleWithGuidelinesProps) => {
    const navigate = useNavigate();
    if (nasemLevel !== 'low' || !displayNasemLevel) return <></>;

    return (
      <Box
        sx={{
          ...boxesStyles,
          width: '100%',
          display: { xs: 'none', md: 'flex' },
          alignItems: 'flex-start',
          gap: { xs: '17px', md: '5px' },
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: '12px',
            color: theme.custom.report.pfasAdviceGuidance.pfasLowNasemLevelAdvice.color,
            fontWeight: 400,
            marginTop: '12px',
            marginRight: '10px',
            paddingLeft: { md: '10px' },
          }}
        >
          If you're still interested in lowering your PFAS levels, refer to our how to guide.
        </Typography>
        <BorderedButton
          sxProps={{
            display: 'flex',
            padding: '7px 14px 7px 18px',
            justifyContent: 'center',
            alignItems: 'center',
            alignSelf: 'stretch',
            width: { xs: '100%', sm: '50%', md: '238px' },
            '& .MuiButton-startIcon': {
              height: '18px',
              width: '18px',
              backgroundImage: `url(${theme.custom.report.resultCTAOptions.viewYourLatestResults.defaultIcon})`,
            },
            '&:hover': {
              background: 'transparent',
              color: theme.buttons.ghost.hover.color,
              border: '2px solid',
              '& .MuiButton-startIcon': {
                height: '18px',
                width: '18px',
                backgroundImage: `url(${theme.custom.report.resultCTAOptions.viewYourLatestResults.hoverIcon})`,
              },
            },
          }}
          additionalProps={{
            style: { borderRadius: '100px', height: '40px' },
            startIcon: <></>,
          }}
          onClick={() => navigate('/pfas-info.html')}
          buttonText="Understand your Test"
        />
      </Box>
    );
  };

  const PFASMediumNasemLevelAdvice = ({ nasemLevel }: { nasemLevel: string }) => {
    if (nasemLevel !== 'medium') return <></>;

    return (
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{
          justifyContent: 'space-between',
          alignItems: 'stretch',
          textAlign: 'left',
        }}
      >
        {mediumNasemLevelAdvices.map((adviceFn, index) => (
          <Box sx={boxesStyles} key={index}>
            {adviceFn()}
          </Box>
        ))}
      </Stack>
    );
  };

  const PFASHighNasemLevelAdvice = ({ nasemLevel }: { nasemLevel: string }) => {
    if (nasemLevel !== 'high') return <></>;

    return (
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        flexWrap="wrap"
        gap={'8px 16px'}
        sx={{
          justifyContent: 'center',
          mb: 1,
          width: '100%',
          textAlign: 'left',
        }}
      >
        <Box sx={boxesStyles}>
          <Typography
            variant="body2"
            sx={{
              fontSize: '12px',
              color: theme.custom.report.pfasAdviceGuidance.pfasHighNasemLevelAdvice.color,
              fontWeight: 400,
            }}
          >
            Prioritize screening for dyslipidemia with a lipid panel following &nbsp;
            <a
              rel="noreferrer"
              style={{ color: theme.custom.report.pfasAdviceGuidance.pfasHighNasemLevelAdvice.linkColor }}
              target="_blank"
              href="https://www.heart.org/"
            >
              American Heart Association (AHA)
            </a>{' '}
            &nbsp; guidance for high-risk adults.
          </Typography>
        </Box>

        <Box sx={boxesStyles}>
          <Typography
            variant="body2"
            sx={{
              fontSize: '12px',
              fontWeight: 400,
              color: theme.custom.report.pfasAdviceGuidance.pfasHighNasemLevelAdvice.atAllWellVisitColor,
            }}
          >
            At all well visits
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: '12px',
              color: theme.custom.report.pfasAdviceGuidance.pfasHighNasemLevelAdvice.color,
              fontWeight: 400,
            }}
          >
            Conduct thyroid function testing (for patients over age 18) with serum thyroid stimulating hormone (TSH)
          </Typography>
        </Box>

        <Box sx={boxesStyles}>
          <Typography
            variant="body2"
            sx={{
              fontSize: '12px',
              fontWeight: 400,
              color: theme.custom.report.pfasAdviceGuidance.pfasHighNasemLevelAdvice.atAllWellVisitColor,
            }}
          >
            At all well visits
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: '12px',
              color: theme.custom.report.pfasAdviceGuidance.pfasHighNasemLevelAdvice.color,
              fontWeight: 400,
            }}
          >
            Assess for signs and symptoms of kidney cancer (if you are over 45), including with urinalysis
          </Typography>
        </Box>

        <Box sx={boxesStyles}>
          <Typography
            variant="body2"
            sx={{
              fontSize: '12px',
              fontWeight: 400,
              color: theme.custom.report.pfasAdviceGuidance.pfasHighNasemLevelAdvice.atAllWellVisitColor,
            }}
          >
            At all well visits
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: '12px',
              color: theme.custom.report.pfasAdviceGuidance.pfasHighNasemLevelAdvice.color,
              fontWeight: 400,
            }}
          >
            Assess for signs and symptoms of testicular cancer and ulcerative colitis
          </Typography>
        </Box>

        <Box sx={boxesStyles}>{renderHypertensiveAdvice()}</Box>
        <Box sx={boxesStyles}>{renderBreastCancerAdvice()}</Box>
      </Stack>
    );
  };

  return (
    <>
      <PFASLowNasemLevelAdvice nasemLevel={nasemLevel} displayNasemLevel={displayNasemLevel} />
      <PFASMediumNasemLevelAdvice nasemLevel={nasemLevel} />
      <PFASHighNasemLevelAdvice nasemLevel={nasemLevel} />
    </>
  );
}
